import { useCallback, useMemo } from "react";
import { Card, CardBody, Col, Container, Row, Form } from "reactstrap";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { BackLink, Input, MapAreaBox } from "../../../../components/Common";
import HeaderBox from "../header-box/index";
import LocationMap from "../map";
import validationSchema from "./validationSchema";

import { getCombinedGeometry } from "../../../../components/Common/Map/helper";
import { getInternalDate } from "../../../../helpers/date-time-helper";
import { TJob } from "../../../../common/types/job";
import Select from "react-select";

import "./styles.scss";
import { TMachine } from "src/common/types";
import { MachineOption } from "src/common/types/machineOption";
import useAuthUser from "src/common/hooks/authUser";
import { API_URL } from "src/api/config";

interface Props {
  data: TJob | null;
  isSaving: boolean;
  isEditMode: boolean;
  onCancel: () => void;
  onSave: (_formData: object) => void;
}

const JobFormStep1 = ({ data, isEditMode, isSaving, onSave }: Props) => {
  const [machineOptions, setMachineOptions] = useState<MachineOption[]>([]);
  const [isLoadingMachines, setIsLoadingMachines] = useState(false);
  const { user } = useAuthUser();

  const form = useFormik({
    initialValues: {
      contactFirstName: data?.contactFirstName || "",
      contactLastName: data?.contactLastName || "",
      contactEmail: data?.contactEmail || "",
      contactPhone: data?.contactPhone || "",

      company: data?.company || "",
      companyType: data?.companyType || "",
      machineApplication: data?.machineApplication || "",

      name: data?.name || "",
      startDate: data?.startDate ? getInternalDate(data.startDate) : "",
      machines: data?.machines || [],

      locationSearchTerm: data?.locationSearchTerm || "",
      siteElevation: data?.siteElevation || "",
      location: data?.location || "",

      geometry: null,

      description: data?.description || "",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: () => {
      onSave(getFormData());
    },
  });

  const selectedMachineOptions = useMemo(
    () => machineOptions.filter((o) => form.values.machines.includes(o.value)),
    [machineOptions, form.values.machines]
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchMachines = async () => {
      try {
        setIsLoadingMachines(true);
        const endpoint = `${API_URL}/Device/GetDevicesForUser?userId=${user.id}`;
        const response = await fetch(endpoint, { signal });
        const responseBody = await response.json();
        const machines: TMachine[] = responseBody.data;

        const options = machines.map((machine) => ({
          value: machine.id.toString(),
          label: `${machine.type} — ${machine.serialNumber}`,
        }));

        setMachineOptions(options);
      } catch (error) {
        if (error.name === "AbortError") {
          console.error("Fetch DevicesForUser Aborted");
        } else {
          console.error("Failed to load machines: ", error);
        }

        setMachineOptions([]);
      } finally {
        setIsLoadingMachines(false);
      }
    };

    fetchMachines();

    return () => {
      controller.abort();
    };
  }, []);

  const isButtonDisabled = useMemo(() => isSaving || !!Object.keys(form.errors).length, [form, isSaving]);

  const setGraphics = useCallback(
    (graphics) => {
      form.setFieldTouched("geometry", true);
      form.setFieldValue("geometry", graphics?.length ? graphics : null, true);
    },
    [form.setFieldValue]
  );

  const getFormData = () => ({
    data: {
      ...form.values,
      geometry: getCombinedGeometry(form.values.geometry!),
    },
  });

  const onLocationChanged = (location) => {
    form.setFieldValue("location", location);
  };

  return (
    <>
      <BackLink />
      <HeaderBox title={isEditMode ? "Edit job" : "Create a new job"} />

      <Card>
        <CardBody>
          <Container fluid>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
                return false;
              }}
            >
              <Row className="mb-4">
                <Col xs={12} className="font-size-18 fw-bold pb-1 text-uppercase">
                  Job Contact Information
                </Col>
                <Col xs={12} className="font-size-12 fw-light pb-3">
                  * Required field
                </Col>
                <Col xs={12} xl={6}>
                  <div className="mb-3">
                    <Input
                      label="Contact First Name"
                      afterLabel=" *"
                      name="contactFirstName"
                      placeholder="Contact First Name"
                      form={form}
                      value={form.values.contactFirstName}
                      disabled={isSaving}
                    />
                  </div>
                </Col>
                <Col xs={12} xl={6}>
                  <div className="mb-3">
                    <Input
                      label="Contact Last Name"
                      afterLabel=" *"
                      name="contactLastName"
                      placeholder="Contact Last Name"
                      form={form}
                      value={form.values.contactLastName}
                      disabled={isSaving}
                    />
                  </div>
                </Col>
                <Col xs={12} xl={6}>
                  <div className="mb-3">
                    <Input
                      label="Contact Email Address"
                      afterLabel=" *"
                      name="contactEmail"
                      placeholder="Contact Email Address"
                      form={form}
                      value={form.values.contactEmail}
                      disabled={isSaving}
                    />
                  </div>
                </Col>
                <Col xs={12} xl={6}>
                  <div className="mb-3">
                    <Input
                      label="Contact Phone Number"
                      afterLabel=" *"
                      name="contactPhone"
                      placeholder="Contact Phone Number"
                      form={form}
                      value={form.values.contactPhone}
                      disabled={isSaving}
                    />
                  </div>
                </Col>
                <Col xs={12} xl={6}>
                  <div className="mb-3">
                    <Input
                      label="Company"
                      afterLabel=" *"
                      name="company"
                      placeholder="Company"
                      form={form}
                      value={form.values.company}
                      disabled={isSaving}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                  Job Details
                </Col>
                <Col xs={12} xl={6}>
                  <div className="mb-3">
                    <Input
                      label="Job Name"
                      afterLabel=" *"
                      name="name"
                      placeholder="Job Name"
                      form={form}
                      value={form.values.name}
                      disabled={isSaving}
                    />
                  </div>
                </Col>
                <Col xs={6} xl={4}>
                  <Input
                    label="Job Start Date"
                    name="startDate"
                    type="date"
                    className="mb-4"
                    form={form}
                    value={form.values.startDate}
                    disabled={isSaving}
                  />
                </Col>
              </Row>
              <hr />
              <Row>
                <Col xs={6} xl={4}>
                  <div className="mb-3">
                    <label className="form-label">
                      Machines<span className="text-danger"> *</span>
                    </label>

                    <Select
                      isMulti
                      options={machineOptions}
                      isLoading={isLoadingMachines}
                      isDisabled={isSaving || isLoadingMachines}
                      placeholder={isLoadingMachines ? "Loading machines..." : "Select machines..."}
                      value={selectedMachineOptions}
                      onChange={(selected) => {
                        const values = selected ? selected.map((o) => o.value) : [];
                        form.setFieldValue("machines", values);
                      }}
                      onBlur={() => form.setFieldTouched("machines", true)}
                      classNamePrefix="react-select"
                      aria-invalid={Boolean(form.touched.machines && form.errors.machines)}
                    />

                    {form.touched.machines && form.errors.machines && <div className="text-danger small mt-1">{form.errors.machines}</div>}
                  </div>
                </Col>
                <Col xs={6} xl={4}>
                  <div className="mb-3">
                    <Input
                      label="Location"
                      name="locationSearchTerm"
                      placeholder="Type in Zip Code or Address..."
                      form={form}
                      value={form.values.locationSearchTerm}
                      disabled={isSaving}
                    />
                    <Input name="location" type="hidden" form={form} value={form.values.location} />
                  </div>
                </Col>
                <Col xs={6} xl={4}>
                  <Input
                    label="Site Elevation"
                    name="siteElevation"
                    placeholder="Site Elevation"
                    className="mb-4"
                    form={form}
                    value={form.values.siteElevation}
                    disabled={isSaving}
                  />
                </Col>
                {/*<Col xs={6} xl={4}>
                                    <Input
                                        label="Total Job Area"
                                        name="totalArea"
                                        placeholder="Total Job Area"
                                        className="mb-4"
                                        form={form}
                                        value={form.values.totalArea}
                                        disabled={isSaving}
                                    />
                                </Col>*/}
              </Row>

              <Row>
                <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                  Location Details
                </Col>
                <Col xs={12} xl={9}>
                  <div className="mb-3" style={{ height: "400px" }}>
                    {(data || !isEditMode) && form && (
                      <LocationMap
                        searchTerm={form.values.locationSearchTerm}
                        geometry={data?.geometry}
                        setGraphics={setGraphics}
                        onLocationChanged={onLocationChanged}
                      />
                    )}
                  </div>
                </Col>
                <Col xs={12} xl={3} style={{ height: "400px" }}>
                  <MapAreaBox geometry={form.values.geometry} errorMessage={form.errors.geometry} />
                </Col>
              </Row>
              <hr />
              <Row>
                <Col xs={12} xl={12}>
                  <div className="mb-3">
                    <Input
                      label="Job/Location Description"
                      name="description"
                      placeholder="Description of the job details and aspects of the location."
                      type="textarea"
                      rows={6}
                      form={form}
                      value={form.values.description}
                      disabled={isSaving}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="text-center mt-3">
                    <button type="submit" className="btn btn-danger save-user" disabled={isButtonDisabled}>
                      {isEditMode ? "UPDATE JOB" : "CREATE JOB"}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Container>
        </CardBody>
      </Card>
    </>
  );
};

export default JobFormStep1;
